import { DEFAULT_LANGUAGE, isSupportedLanguage, normalizeLanguage } from './config'

export const LANGUAGE_STORAGE_KEY = 'praysely.language'
export const LANGUAGE_EXPLICIT_STORAGE_KEY = 'praysely.languageExplicit'

const getDefaultStorage = () =>
  typeof window === 'undefined' ? null : window.localStorage

export const getStoredLanguage = (storage = getDefaultStorage()) => {
  if (!storage || storage.getItem(LANGUAGE_EXPLICIT_STORAGE_KEY) !== 'true') return null
  return normalizeLanguage(storage.getItem(LANGUAGE_STORAGE_KEY))
}

export const getBrowserLanguage = (
  browser = typeof navigator === 'undefined' ? undefined : navigator
) => {
  const requestedLanguages = browser?.languages?.length
    ? browser.languages
    : [browser?.language]

  for (const value of requestedLanguages) {
    const language = normalizeLanguage(value)
    if (language) return language
  }

  return DEFAULT_LANGUAGE
}

export const getAccountLanguage = (account) =>
  normalizeLanguage(account?.preferences?.language)

export const getPersistedAccount = (storage = getDefaultStorage()) => {
  if (!storage) return null
  try {
    return JSON.parse(storage.getItem('user'))
  } catch {
    return null
  }
}

export const resolveLanguage = ({ account, storage, browser } = {}) =>
  getAccountLanguage(account) ||
  getStoredLanguage(storage) ||
  getBrowserLanguage(browser) ||
  DEFAULT_LANGUAGE

export const resolveInitialLanguage = ({ storage, browser } = {}) =>
  resolveLanguage({
    account: getPersistedAccount(storage),
    storage,
    browser,
  })

export const persistLanguage = (language, storage = getDefaultStorage()) => {
  if (!storage || !isSupportedLanguage(language)) return
  storage.setItem(LANGUAGE_STORAGE_KEY, language)
  storage.setItem(LANGUAGE_EXPLICIT_STORAGE_KEY, 'true')
}

export const applyLanguage = async (
  language,
  i18nInstance,
  storage = getDefaultStorage()
) => {
  if (!isSupportedLanguage(language)) return false

  await i18nInstance.changeLanguage(language)
  persistLanguage(language, storage)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language
    document.documentElement.dir = 'ltr'
  }
  return true
}

export const changeAppLanguage = async ({
  language,
  isAuthenticated,
  updateRemote,
  updateAccount,
  i18nInstance,
  storage,
}) => {
  if (!isSupportedLanguage(language)) {
    throw new Error(`Unsupported language: ${language}`)
  }

  let appliedLanguage = language
  let preferences = null

  if (isAuthenticated) {
    const response = await updateRemote(language)
    preferences = response?.preferences
    appliedLanguage = normalizeLanguage(preferences?.language)

    if (!appliedLanguage) {
      throw new Error('The preferences API returned an unsupported language')
    }
  }

  await applyLanguage(appliedLanguage, i18nInstance, storage)
  if (preferences) updateAccount(preferences)

  return appliedLanguage
}
